import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import "./Loginpage.css";
import loginBgImage from "../../assets/images/Login_Page_bg.png";
import logoImage from "../../assets/images/LogoImage.svg";
import { callApi, setTokens } from "../../utils/apiService";
import { videoCreatorEndPoint } from "../../Constants/constants";
import { useUser } from "../../Context/UserContext";
import { fetchUserData } from "../../utils/fetchUserData";
import { showToast } from "../../utils/toastUtils";

const LoginPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  });

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { setUser, setDashboardData } = useUser();

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.email || !formData.password) {
      showToast("error", "Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const response = await callApi(
        `${videoCreatorEndPoint}auth/login`,
        "POST",
        {
          email: formData.email,
          password: formData.password,
        },
      );

      if (response.status) {
        // Store tokens
        const { accessToken, refreshToken } = response.data;
        setTokens({ accessToken, refreshToken });

        await fetchUserData(setUser, setDashboardData);

        navigate("/dashboard", { replace: true });
        showToast("success", "Login successful! Welcome back.", 100);
      } else {
        if (response?.alert === "Failed to fetch") {
          showToast(
            "error",
            "Unable to connect to the server. Please try again later.",
          );
        } else
          showToast("error", response.alert || response.message || "Login failed");
      }
    } catch (error) {
      if (error?.message === "Failed to fetch") {
        showToast("error", "Unable to connect to the server. Please try again later.");
      } else showToast("error", error.message || "Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* LEFT SIDE IMAGE */}
      <div
        className="login-left"
        style={{ backgroundImage: `url(${loginBgImage})` }}
      >
        {/* MOBILE BOTTOM WAVE */}
        <div className="mobile-wave">
          <svg
            className="mobile-wave-svg"
            viewBox="0 0 1440 320"
            preserveAspectRatio="none"
          >
            <path
              fill="#f5f5f5"
              d="M0,224L80,202.7C160,181,320,139,480,154.7C640,171,800,245,960,250.7C1120,256,1280,192,1360,160L1440,128L1440,320L1360,320C1280,320,1120,320,960,320C800,320,640,320,480,320C320,320,160,320,80,320L0,320Z"
            />
          </svg>
        </div>
      </div>
      {/* MID CURVE (SEPARATE ELEMENT) */}
      <div className="mid-curve">
        <svg
          className="mid-curve-svg"
          viewBox="0 0 520 1000"
          preserveAspectRatio="none"
        >
          <path
            d="
              M 520 0
              L 270 0
              C 220 70, 205 140, 245 210
              C 310 330, 175 430, 140 520
              C 100 640, 260 730, 275 840
              C 290 930, 225 1000, 170 1000
              L 520 1000
              Z
            "
            fill="#f5f5f5"
          />
        </svg>
      </div>

      {/* Right side */}
      <div className="login-right">
        <div className="login-card">
          <div className="login-logo">
            <img src={logoImage} alt="AstroCure" className="login-logo-image" />
          </div>

          <h1 className="login-title">Welcome To AstroCure!</h1>

          <form onSubmit={handleSubmit} className="login-form">
            <div className="form-group">
              <div className="login-text">Login</div>
              <div className="login-desc">Login to access your account</div>
              <div className="floating-group">
                <label htmlFor="email" className="floating-label">
                  V-ID <span className="required">*</span>
                </label>

                <input
                  type="email"
                  id="email"
                  name="email"
                  className="form-input floating-input"
                  placeholder="Enter your email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={loading}
                />
              </div>
            </div>
            <div className="form-group">
              <div className="floating-group">
                <label htmlFor="password" className="floating-label">
                  V-Password <span className="required">*</span>
                </label>

                <div className="password-wrapper">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    className="form-input floating-input"
                    placeholder="Enter your Password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    disabled={loading}
                  />

                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword((prev) => !prev)}
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                    disabled={loading}
                  >
                    {showPassword ? (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <line
                          x1="1"
                          y1="1"
                          x2="23"
                          y2="23"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    ) : (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                        <circle
                          cx="12"
                          cy="12"
                          r="3"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="form-footer">
              <label className="remember-me">
                <input
                  type="checkbox"
                  name="rememberMe"
                  checked={formData.rememberMe}
                  onChange={handleInputChange}
                />
                <span>Remember Me</span>
              </label>

              <Link to="/forgot-password" className="forgot-password">
                Forgot Password ?
              </Link>
            </div>

            <button type="submit" className="login-button" disabled={loading}>
              Login
            </button>
          </form>
        </div>

        <footer className="login-footer">
          Copyright © 2026 Astrovidhi Private Limited All rights reserved
        </footer>
      </div>
    </div>
  );
};

export default LoginPage;
